import type {Ability, CastingTimeUnit, FeatCategory} from '../../../../generated/types';
import type {ChooseGroup, FeatureProps, SpellTable} from './types';

export type FeatureData = {
	id: string;
	title: string;
	description: string;
	/** уровни, на которых особенность появляется или усиливается; пусто — с 1-го */
	levels: number[];
	order: number;
	/** вид особенности для выборов вроде манёвров и инвокаций */
	type?: string;
	props?: FeatureProps;
	choose: ChooseGroup[];
	classId?: string;
	subClassId?: string;
	specieId?: string;
	featId?: string;
};

export type ClassTable = SpellTable;

export type ClassData = {
	id: string;
	title: string;
	/** название в родительном падеже: «уровень Барда» */
	genitiveTitle: string;
	hitDie: number;
	saves: Ability[];
	/** владения класса в формате props особенностей */
	props: FeatureProps;
	choose: ChooseGroup[];
	table: ClassTable;
	subClasses: {id: string; title: string}[];
	multiclassRequirement?: MulticlassRequirement;
	/** стартовое снаряжение текстом: «(А) … ; (Б) 155 ЗМ» */
	equipment?: string;
};

export type MulticlassRequirement = {
	abilities: Ability[];
	/** хватает одной характеристики из списка: Воину Сила или Ловкость */
	any?: boolean;
	score: number;
};

export type SpecieData = {
	id: string;
	title: string;
	speed: number;
	sizes: string[];
};

export type OriginToolChoice = 'tool' | 'instrument' | 'gamingSet';

export type OriginData = {
	id: string;
	title: string;
	abilities: Ability[];
	skills: string[];
	/** инструмент, которым происхождение даёт владение */
	toolId?: string;
	/** вид инструмента, если его нужно выбрать */
	toolChoice?: OriginToolChoice;
	featId?: string;
	/** вариант черты: значение группы с ключом `variantKey` черты */
	featVariant?: string;
	/** категория черты буквой, если черту нужно выбрать */
	featCategory?: string;
	/** стартовое снаряжение текстом: «(А) … ; (Б) 50 ЗМ» */
	equipment?: string;
};

export type FeatData = {
	id: string;
	title: string;
	category: FeatCategory;
	/** ключ группы выбора, которую предустанавливает вариант черты у происхождения */
	variantKey?: string;
	repeatable: boolean;
};

export type SpellCastingTime = {
	unit: CastingTimeUnit;
	amount: number;
};

export type SpellData = {
	id: string;
	title: string;
	level: number;
	school?: string;
	classIds: string[];
	castingTime?: SpellCastingTime;
	ritual: boolean;
	concentration: boolean;
	attackType?: string;
	damageTypes: string[];
	savingThrows: Ability[];
	/** что получает цель, пока заклинание действует */
	props?: FeatureProps;
	choose: ChooseGroup[];
	/** кости урона или лечения из описания */
	dice?: SpellDice;
	/** дистанция текстом: «120 футов», «На себя (15-футовый Конус)» */
	rangeText: string;
	/** длительность текстом: «Концентрация, вплоть до 1 минуты» */
	durationText: string;
	components: SpellComponents;
};

export type SpellComponents = {
	verbal: boolean;
	somatic: boolean;
	/** материальный компонент */
	material?: string;
};

export type SpellDice = {
	kind: 'damage' | 'healing';
	/** кости на круге заклинания: 4d6 */
	base: string;
	/** прибавка за каждый круг ячейки выше */
	perSlot?: string;
	/** к броску прибавляется модификатор заклинательной характеристики */
	addsModifier: boolean;
	/** кости фокуса по уровню персонажа */
	byCharacterLevel?: Record<string, string>;
};

export type ItemData = {
	id: string;
	title: string;
	type?: string;
	subTypes: string[];
	weaponCategory?: string;
	weaponMastery: string[];
};

export type LanguageData = {
	id: string;
	title: string;
	standard?: boolean | null;
	exotic?: boolean | null;
};

export type CatalogData = {
	classes: ClassData[];
	species: SpecieData[];
	origins: OriginData[];
	feats: FeatData[];
	features: FeatureData[];
	spells: SpellData[];
	items: ItemData[];
	languages: LanguageData[];
};

const indexById = <T extends {id: string}>(list: T[]) => new Map(list.map((entry) => [entry.id, entry]));

const groupBy = <T>(list: T[], keyOf: (entry: T) => string | undefined) => {
	const groups = new Map<string, T[]>();
	for (const entry of list) {
		const key = keyOf(entry);
		if (key) groups.set(key, [...(groups.get(key) || []), entry]);
	}
	return groups;
};

export class Catalog {
	readonly classes: ClassData[];
	readonly species: SpecieData[];
	readonly origins: OriginData[];
	readonly feats: FeatData[];
	readonly features: FeatureData[];
	readonly spells: SpellData[];
	readonly items: ItemData[];
	readonly languages: LanguageData[];

	private readonly classesById: Map<string, ClassData>;
	private readonly speciesById: Map<string, SpecieData>;
	private readonly originsById: Map<string, OriginData>;
	private readonly featsById: Map<string, FeatData>;
	private readonly featuresById: Map<string, FeatureData>;
	private readonly spellsById: Map<string, SpellData>;
	private readonly itemsById: Map<string, ItemData>;
	private readonly languagesById: Map<string, LanguageData>;
	private readonly featuresByClass: Map<string, FeatureData[]>;
	private readonly featuresBySubClass: Map<string, FeatureData[]>;
	private readonly featuresBySpecie: Map<string, FeatureData[]>;
	private readonly featuresByFeat: Map<string, FeatureData[]>;

	constructor(data: CatalogData) {
		this.classes = data.classes;
		this.species = data.species;
		this.origins = data.origins;
		this.feats = data.feats;
		this.features = data.features;
		this.spells = data.spells;
		this.items = data.items;
		this.languages = data.languages;

		this.classesById = indexById(data.classes);
		this.speciesById = indexById(data.species);
		this.originsById = indexById(data.origins);
		this.featsById = indexById(data.feats);
		this.featuresById = indexById(data.features);
		this.spellsById = indexById(data.spells);
		this.itemsById = indexById(data.items);
		this.languagesById = indexById(data.languages);
		this.featuresByClass = groupBy(data.features, (feature) => feature.classId);
		this.featuresBySubClass = groupBy(data.features, (feature) => feature.subClassId);
		this.featuresBySpecie = groupBy(data.features, (feature) => feature.specieId);
		this.featuresByFeat = groupBy(data.features, (feature) => feature.featId);
	}

	getClass = (id: string) => this.classesById.get(id);
	getSpecie = (id: string) => this.speciesById.get(id);
	getOrigin = (id: string) => this.originsById.get(id);
	getFeat = (id: string) => this.featsById.get(id);
	getFeature = (id: string) => this.featuresById.get(id);
	getSpell = (id: string) => this.spellsById.get(id);
	getItem = (id: string) => this.itemsById.get(id);
	getLanguage = (id: string) => this.languagesById.get(id);

	classFeatures = (classId: string) => this.featuresByClass.get(classId) || [];
	subClassFeatures = (subClassId: string) => this.featuresBySubClass.get(subClassId) || [];
	specieFeatures = (specieId: string) => this.featuresBySpecie.get(specieId) || [];
	featFeatures = (featId: string) => this.featuresByFeat.get(featId) || [];

	subClassTitle = (subClassId: string) =>
		this.classes.flatMap((classData) => classData.subClasses).find((subClass) => subClass.id === subClassId)?.title;
}
